/**
 * Created by Ning Jiang on 2021-06-23.
 */

import {gsap} from "gsap";
import {IndividualWinView} from "./IndividualWinView";
import {Serial} from "../../Serial";
import {IParsedBetWayWinData} from "../../server/data/SerialParsedGameData";
import {SerialGameConfig} from "../../SerialGameConfig";

export class IndividualWinController {

    private _view:IndividualWinView;
    private _countUp:PIXI.Container;
    private _currentTimeline:gsap.core.Timeline | null;
    private _stopped:boolean;
    private _winCap:boolean;

    constructor() {
        this._view = new IndividualWinView();
    }

    public start():void {
        if (Serial.isActionSpinsActive){
            return;
        }
        this._stopped = false;
        const data = Serial.model.parsedGameData;
        this._countUp = data.currentWinRatioConfig!.bigWinLevel > -1 ? Serial.bigWinCountUp : Serial.initialCountUp;
        this._winCap = data.brokeTheBank && data.betWins.some(w => w.multipliedAmount / data.playedBetValue >= 18700);
        this._view.start();
        if(data.betWins.length > 0) {
            // Add a small delay for the first time so we don't start to play before the autoPlay start next spin.
            this.playNextBetWin(0, 0.5);
        }
    }

    private playNextBetWin(index:number, delay:number = 0):void {
        if(this._stopped) {
            return;
        }

        const data = Serial.model.parsedGameData;
        const winRatio = data.currentWinRatioConfig?.ratio!;
        let nextDelay = 0.8;
        const tl = gsap.timeline();
        if(index === data.betWins.length) {
            tl.add(this.getAllWinAnimation(winRatio), delay);
            nextDelay = 1.2;
        } else {
            if(index >= data.betWins.length) {
                index = 0;
            }
            if((index === 0 && data.betWins.length === 1) || this._winCap) {
                // do not play single if only one line.
            } else {
                tl.add(this.getBetWayAnimation(data.betWins[index], winRatio), delay);
            }
        }
        tl.add(() => {
            this.playNextBetWin(index + 1, nextDelay);
        });
        this._currentTimeline = tl;
    }

    private getBetWayAnimation(winData:IParsedBetWayWinData, winRatio:number):TimelineLite {
        const tl = gsap.timeline();
        tl.add([
            gsap.to(this._countUp, {externalAlpha : 0, duration : 0.2}),
            gsap.to(Serial.waysWon, {externalAlpha : 0, duration : 0.2})
        ], 0);
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            let firstSym = reelId === 2;
            reelWin.forEach((symWin, symId) => {
                if(symWin) {
                    tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio, firstSym, winData.multipliedAmount, winData.numberOfCombinations), 0);
                    firstSym = false;
                }
            });
        });
        return tl;
    }

    private getAllWinAnimation(winRatio:number):TimelineLite {
        const tl = gsap.timeline();
        tl.add([
            gsap.to(this._countUp, {externalAlpha : 1, duration : 0.2}),
            gsap.to(Serial.waysWon, {externalAlpha : 1, duration : 0.2}),
            () => {this._view.removeWinText()}
        ], 0);
        Serial.model.parsedGameData.winPositions.forEach((reelWin, reelId) => {
            reelWin.forEach((symWin, symId) => {
                if(symWin) {
                    tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio, false), 0);
                }
            });
        });

        return tl;
    }

    private getSymbolWinAnimation(reelId:number, symId:number, winRatio:number, showWinText:boolean, winAmount?:number, winWays?:number):TimelineLite {
        const tl = gsap.timeline();
        const sym = Serial.getSymbol(reelId, symId, false);
        tl.add(() => {
            if(!this._stopped && sym.transform) {
                Serial.getReelView(reelId, symId).addWinSymbol(sym);
            }
        });// no need to have win state in this game?

        tl.add(this._view.playAnimation(sym.symbolHeight, sym.getGlobalPosition(), this.getEffectLevel(winRatio), showWinText, winAmount, winWays));

        return tl;
    }

    private getEffectLevel(winRatio:number):number {
        for(let i = 1; i < 4; i++) {
            if(winRatio < SerialGameConfig.WIN_RATIOS[i].ratio) {
                return i - 1;
            }
        }
        return 3;
    }

    public async stop():Promise<any> {
        this._stopped = true;
        if(this._currentTimeline && this._currentTimeline.isActive()) {
            await this._currentTimeline.progress(1);
            this._currentTimeline.kill();
        }
        this._currentTimeline = null;
        this._view.stop();
        await Promise.resolve();
    }
}